import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Loader2, User } from "lucide-react";
import { motion } from "framer-motion";
import BlogContent from "../../components/BlogContent";


const siteUrl = "https://www.sevaafoundation.com";
const backendUrl = `${siteUrl}/seva-backend`;
const defaultImage = `${siteUrl}/og-blogs.jpg`;
const logoImage = `${siteUrl}/logo512.png`;

type BlogData = {
  id: number | string;
  title: string;
  slug?: string;
  category?: string;
  short_description?: string;
  content: string;
  author_name?: string;
  created_at?: string;
  updated_at?: string;
  published_at?: string;
  cover_image?: string;
  seo_title?: string;
  meta_title?: string;
  meta_description?: string;
  seo_description?: string;
  seo_keywords?: string;
  image_alt?: string;
};

const stripText = (text?: string) =>
  (text || "")
    .replace(/^#{2,3}\s+/gm, "")
    .replace(/^[-*]\s+/gm, "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();

const limitText = (text: string, maxLength: number) => {
  const clean = stripText(text);
  return clean.length <= maxLength ? clean : `${clean.slice(0, maxLength - 1).trim()}…`;
};

const getAbsoluteImage = (url?: string) => {
  if (!url) return defaultImage;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/uploads/")) return `${backendUrl}${url}`;
  return `${siteUrl}${url.startsWith("/") ? "" : "/"}${url}`;
};

const formatDate = (date?: string) => {
  if (!date) return "SEVAA Foundation";

  return new Date(date).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const countWords = (text: string) => stripText(text).split(/\s+/).filter(Boolean).length;

const BlogDetail = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState<BlogData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    let isMounted = true;

    fetch(`${backendUrl}/api/blogs/public-preview.php?id=${encodeURIComponent(id || "")}`, { cache: "no-store" })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.message || "Blog not found.");
        }
        return data;
      })
      .then((data) => {
        if (isMounted) setBlog(data.blog);
      })
      .catch((fetchError) => {
        if (isMounted) {
          setError(fetchError instanceof Error ? fetchError.message : "Blog could not be loaded.");
        }
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [id]);

  const seo = useMemo(() => {
    if (!blog) return null;

    const identifier = blog.slug || blog.id || id;
    const pageUrl = `${siteUrl}/blog/${identifier}`;
    const blogTitle = stripText(blog.title) || "SEVAA Foundation Blog";
    const seoTitle = limitText(blog.seo_title || blog.meta_title || blogTitle, 52);
    const metaDescription = limitText(
      blog.meta_description ||
        blog.seo_description ||
        blog.short_description ||
        blog.content ||
        `Read ${blogTitle} from SEVAA Foundation.`,
      160
    );
    const publishedDate = blog.published_at || blog.created_at || new Date().toISOString();
    const modifiedDate = blog.updated_at || publishedDate;
    const heroImage = getAbsoluteImage(blog.cover_image);
    const authorName = blog.author_name || "SEVAA Foundation";

    return {
      pageUrl,
      blogTitle,
      seoTitle,
      metaDescription,
      publishedDate,
      modifiedDate,
      heroImage,
      authorName,
    };
  }, [blog, id]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-orange-600" size={42} />
      </div>
    );
  }

  if (!blog || !seo || error) {
    return (
      <>
        <Helmet>
          <title>Blog Not Found | SEVAA Foundation</title>
          <meta name="description" content="The requested SEVAA Foundation blog could not be found." />
          <meta name="robots" content="noindex, follow" />
        </Helmet>

        <main className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-6 text-center">
          <h1 className="mb-3 text-3xl font-black text-slate-900">Blog Not Found</h1>
          <p className="mb-7 text-slate-600">{error || "This article is unavailable."}</p>
          <Link to="/blogs" className="rounded-full bg-orange-600 px-6 py-3 font-bold text-white hover:bg-orange-700">
            Back to Blogs
          </Link>
        </main>
      </>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${seo.pageUrl}#blogposting`,
    headline: seo.blogTitle,
    name: seo.blogTitle,
    description: seo.metaDescription,
    image: [seo.heroImage],
    url: seo.pageUrl,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": seo.pageUrl,
    },
    datePublished: seo.publishedDate,
    dateModified: seo.modifiedDate,
    inLanguage: "en-IN",
    isAccessibleForFree: true,
    articleSection: blog.category || "SEVAA Foundation NGO Blog",
    keywords: blog.seo_keywords || blog.category,
    wordCount: countWords(blog.content),
    articleBody: stripText(blog.content),
    author: {
      "@type": "Organization",
      "@id": `${siteUrl}/#ngo`,
      name: seo.authorName,
      url: siteUrl,
    },
    publisher: {
      "@type": "NGO",
      "@id": `${siteUrl}/#ngo`,
      name: "SEVAA Foundation",
      alternateName: "Swadesh Sevaa Charitable Foundation",
      url: siteUrl,
      logo: {
        "@type": "ImageObject",
        url: logoImage,
      },
    },
    about: blog.category || "Social Welfare",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `${siteUrl}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blogs",
        item: `${siteUrl}/blogs`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: seo.blogTitle,
        item: seo.pageUrl,
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{`${seo.seoTitle} | SEVAA Foundation`}</title>
        <meta name="description" content={seo.metaDescription} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content={seo.authorName} />
        {blog.seo_keywords && <meta name="keywords" content={blog.seo_keywords} />}
        <link rel="canonical" href={seo.pageUrl} />

        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="SEVAA Foundation" />
        <meta property="og:url" content={seo.pageUrl} />
        <meta property="og:title" content={seo.seoTitle} />
        <meta property="og:description" content={seo.metaDescription} />
        <meta property="og:image" content={seo.heroImage} />
        <meta property="og:image:alt" content={blog.image_alt || `${seo.blogTitle} - SEVAA Foundation`} />
        <meta property="og:locale" content="en_IN" />
        <meta property="article:published_time" content={seo.publishedDate} />
        <meta property="article:modified_time" content={seo.modifiedDate} />
        {blog.category && <meta property="article:section" content={blog.category} />}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.seoTitle} />
        <meta name="twitter:description" content={seo.metaDescription} />
        <meta name="twitter:image" content={seo.heroImage} />
        <meta name="twitter:image:alt" content={blog.image_alt || `${seo.blogTitle} - SEVAA Foundation`} />

        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <main className="min-h-screen overflow-x-hidden bg-slate-50 px-6 py-12 font-poppins selection:bg-orange-200 selection:text-orange-950">
        <div className="mx-auto max-w-6xl">
          <Link
            to="/blogs"
            className="mb-8 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-500 shadow-sm transition hover:border-orange-200 hover:text-orange-600"
          >
            <ArrowLeft size={16} /> Back to Stories
          </Link>

          <article itemScope itemType="https://schema.org/BlogPosting">
            <header className="mx-auto mb-10 max-w-4xl text-center md:text-left">
              {blog.category && (
                <span className="mb-5 inline-block rounded-full bg-orange-50 px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-orange-700">
                  {blog.category}
                </span>
              )}

              <h1
                itemProp="headline"
                className="mb-6 text-3xl font-black leading-[1.12] tracking-tight text-slate-950 md:text-5xl lg:text-6xl"
              >
                {seo.blogTitle}
              </h1>

              {blog.short_description && (
                <p className="mb-7 text-lg font-medium leading-8 text-slate-600 md:text-xl">
                  {blog.short_description}
                </p>
              )}

              <div className="flex flex-wrap items-center justify-center gap-3 text-xs font-bold uppercase tracking-wider text-slate-600 md:justify-start">
                <span className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 shadow-sm">
                  <User size={16} className="text-orange-500" />
                  <span itemProp="author">{seo.authorName}</span>
                </span>

                <time
                  itemProp="datePublished"
                  dateTime={seo.publishedDate}
                  className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 shadow-sm"
                >
                  <Calendar size={16} className="text-teal-600" />
                  {formatDate(seo.publishedDate)}
                </time>
              </div>
            </header>

            <motion.figure
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              className="mx-auto mb-14 h-[320px] max-w-5xl overflow-hidden rounded-3xl border border-slate-200 bg-slate-100 shadow-lg md:h-[520px]"
            >
              <img
                itemProp="image"
                src={seo.heroImage}
                alt={blog.image_alt || `${seo.blogTitle} - SEVAA Foundation ${blog.category || "NGO"} article`}
                className="h-full w-full object-cover"
                loading="eager"
                fetchPriority="high"
                width={1200}
                height={630}
              />
            </motion.figure>

            <div className="mx-auto max-w-4xl rounded-3xl border border-slate-100 bg-white px-6 py-10 shadow-sm md:px-12 md:py-14">
              <div itemProp="articleBody">
                <BlogContent content={blog.content} />
              </div>

              <footer className="mt-14 border-t border-slate-200 pt-8">
                <div className="rounded-2xl bg-slate-950 p-6 text-white md:p-8">
                  <h2 className="text-2xl font-black">Stay connected with SEVAA Foundation</h2>
                  <p className="mt-3 max-w-2xl leading-7 text-slate-300">
                    Explore more responsible NGO awareness articles and learn about SEVAA Foundation&apos;s work across its six social welfare focus areas.
                  </p>
                  <Link
                    to="/blogs"
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-orange-500 px-5 py-2.5 text-sm font-black text-white transition hover:bg-orange-600"
                  >
                    Read More Stories <ArrowLeft className="rotate-180" size={16} />
                  </Link>
                </div>
              </footer>
            </div>
          </article>
        </div>
      </main>
    </>
  );
};

export default BlogDetail;
